import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";

import { useUploadLetterTemplate } from "../hooks/useUploadLetterTemplate";
import api from "../../../../api/api";
import CustomSelect from "../../../CoPilotPage/CustomSelect";
import { getCaseId } from "../../../../Utils/helper";
import PR from "../../../../assets/state-svg/puerto-rico";
import GU from "../../../../assets/state-svg/guam";
import MP from "../../../../assets/state-svg/marian";
import VI from "../../../../assets/state-svg/us-virigin-island";

import "./UploadLetterTemplates.css";

const STATE_ICONS = {
  PR,
  GU,
  VI,
  MP,
};

const SPECIAL_STATES = Object.keys(STATE_ICONS);

const STEPS = [
  {
    key: "caseTypes",
    label: "Case Types",
    hint: "Choose which case types can use this letter template.",
  },
  {
    key: "venue",
    label: "Venue & States",
    hint: "Choose where this letter template can be used.",
  },
  {
    key: "page",
    label: "Page & Dropdown",
    hint: "Choose where this template appears inside the case.",
  },
  {
    key: "document",
    label: "Document",
    hint: "Name the template and attach the .docx file.",
  },
];

const GUIDE_STEPS = [
  {
    title: "Start with a blank table",
    body: "Insert a borderless table into your Word document. That table is the template body.",
  },
  {
    title: "Add column titles",
    body: "Put a title in each cell of the first row — the label the reader sees, e.g. Name, Price, Quantity.",
  },
  {
    title: "Loop over the data",
    body: "In the second row, wrap the cells in a Jinja2 loop so one row renders per record.",
    notes: [
      "mpvi is a variable from the Variables Table.",
      "Prefix it with a_ to pull every row for that entity.",
    ],
  },
  {
    title: "Format values",
    body: "Use loop.index to print a running number next to each row.",
  },
  {
    title: "Mind the spacing",
    body: "Leave a space after {% and before %}. Without it the tag will not parse.",
  },
];

/* -------------------------------------------------------------------------- */
/*                               WIZARD HEADER                                */
/* -------------------------------------------------------------------------- */

const StepRail = ({ current, maxUnlockedStep, validity, onJump }) => {
  return (
    <div className="template-wizard-header">
      <ol className="template-wizard-rail">
        {STEPS.map((stepItem, index) => {
          const active = current === index;
          const unlocked = index <= maxUnlockedStep;
          const completed = index < maxUnlockedStep && !validity[stepItem.key];

          let statusClass = "template-wizard-step-locked";

          if (active) {
            statusClass = "template-wizard-step-active";
          } else if (completed) {
            statusClass = "template-wizard-step-completed";
          } else if (unlocked) {
            statusClass = "template-wizard-step-unlocked";
          }

          return (
            <li
              key={stepItem.key}
              className={`template-wizard-step ${index === 0 ? "template-wizard-step-first" : ""} ${
                index === STEPS.length - 1 ? "template-wizard-step-last" : ""
              }`}
            >
              <button
                type="button"
                disabled={!unlocked}
                className={`template-wizard-step-button ${statusClass}`}
                onClick={() => {
                  if (unlocked) {
                    onJump(index);
                  }
                }}
                title={unlocked ? stepItem.hint : `Complete ${STEPS[index - 1]?.label} first`}
                aria-current={active ? "step" : undefined}
              >
                <span className="template-wizard-step-content">
                  <span className="template-wizard-step-number">
                    {completed ? (
                      "✓"
                    ) : unlocked ? (
                      `${index + 1}:`
                    ) : (
                      <span className="template-wizard-lock">🔒</span>
                    )}
                  </span>

                  <span className="template-wizard-label">{stepItem.label}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const SearchBox = ({ value, onChange, placeholder }) => {
  return (
    <input
      type="search"
      className="form-control height-25 p-l-5 template-search-input"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

/* -------------------------------------------------------------------------- */

const EmptyState = ({ children }) => {
  return <div className="template-empty-state">{children}</div>;
};

/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

const UploadLetterTemplates = ({ handleTabChange }) => {
  const caseId = getCaseId();

  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";

  const { uploadLetterTemplate } = useUploadLetterTemplate();

  /* ---------------------------------------------------------------------- */
  /*                                WIZARD                                  */
  /* ---------------------------------------------------------------------- */

  const [step, setStep] = useState(0);

  /*
   * Only steps <= maxUnlockedStep can be opened.
   *
   * This is intentionally separate from field validity.
   * A field becoming valid does NOT automatically unlock future steps.
   * The user must press Continue.
   */
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);

  /* ---------------------------------------------------------------------- */
  /*                              CASE TYPES                                */
  /* ---------------------------------------------------------------------- */

  const [caseTypesByCategory, setCaseTypesByCategory] = useState({});

  const [selectedCaseTypes, setSelectedCaseTypes] = useState([]);

  const [caseTypeQuery, setCaseTypeQuery] = useState("");

  /* ---------------------------------------------------------------------- */
  /*                           JURISDICTION / STATES                         */
  /* ---------------------------------------------------------------------- */

  const [states, setStates] = useState([]);

  const [selectedStates, setSelectedStates] = useState([]);

  const [stateQuery, setStateQuery] = useState("");

  const [jurisdiction, setJurisdiction] = useState("State");

  /* ---------------------------------------------------------------------- */
  /*                           PAGE / DROPDOWN                              */
  /* ---------------------------------------------------------------------- */

  const [options, setOptions] = useState([]);

  const [pageId, setPageId] = useState("");

  const [dropdowns, setDropdowns] = useState([]);

  const [dropdownId, setDropdownId] = useState("");

  const [pagesLoading, setPagesLoading] = useState(false);

  /* ---------------------------------------------------------------------- */
  /*                               DOCUMENT                                 */
  /* ---------------------------------------------------------------------- */

  const [templateName, setTemplateName] = useState("");

  const [uploadedFile, setUploadedFile] = useState(null);

  const [dragging, setDragging] = useState(false);

  /* ---------------------------------------------------------------------- */
  /*                               SUBMIT                                   */
  /* ---------------------------------------------------------------------- */

  const [submitting, setSubmitting] = useState(false);

  const [submitError, setSubmitError] = useState("");

  /* ---------------------------------------------------------------------- */

  const fileInputRef = useRef(null);

  const pagesReq = useRef(0);

  const dropdownsReq = useRef(0);

  const isFederal = jurisdiction === "Federal";

  const allCaseTypeIds = useMemo(
    () => [
      ...new Set(
        Object.values(caseTypesByCategory).flatMap((caseTypes) =>
          (caseTypes || []).map((caseType) => caseType.id)
        )
      ),
    ],
    [caseTypesByCategory]
  );

  const allCaseTypesSelected =
    allCaseTypeIds.length > 0 && allCaseTypeIds.every((id) => selectedCaseTypes.includes(id));

  const allStatesSelected =
    states.length > 0 && states.every((stateItem) => selectedStates.includes(stateItem.id));

  /* ---------------------------------------------------------------------- */
  /*                               VALIDATION                               */
  /* ---------------------------------------------------------------------- */

  const validity = useMemo(
    () => ({
      caseTypes: selectedCaseTypes.length ? "" : "Select at least one case type to continue.",

      venue: isFederal
        ? ""
        : selectedStates.length
          ? ""
          : "Select at least one state, or choose Federal.",

      page: pagesLoading
        ? "Pages are still loading."
        : !options.length
          ? "No pages with dropdowns match the selected case types."
          : !pageId
            ? "Choose the page where this template should appear."
            : !dropdownId
              ? "Choose a dropdown for the selected page."
              : "",

      document: !templateName.trim()
        ? "Enter a template name."
        : !uploadedFile
          ? "Attach a .docx template."
          : "",
    }),
    [
      selectedCaseTypes,
      selectedStates,
      isFederal,
      pagesLoading,
      options,
      pageId,
      dropdownId,
      templateName,
      uploadedFile,
    ]
  );

  const firstInvalidStep = useMemo(
    () => STEPS.findIndex((stepItem) => validity[stepItem.key]),
    [validity]
  );

  const allStepsValid = firstInvalidStep === -1;

  const currentBlocker = validity[STEPS[step].key];

  const isLastStep = step === STEPS.length - 1;

  /* ---------------------------------------------------------------------- */
  /*                         PROGRESSION MANAGEMENT                         */
  /* ---------------------------------------------------------------------- */

  const lockAfterStep = useCallback((stepIndex) => {
    setMaxUnlockedStep((previous) => Math.min(previous, stepIndex));
  }, []);

  /*
   * Safety net:
   * if asynchronous data invalidates an earlier step,
   * push the user back to that step and lock everything after it.
   */
  useEffect(() => {
    if (firstInvalidStep !== -1 && firstInvalidStep < maxUnlockedStep) {
      setMaxUnlockedStep(firstInvalidStep);

      if (step > firstInvalidStep) {
        setStep(firstInvalidStep);
      }
    }
  }, [firstInvalidStep, maxUnlockedStep, step]);

  const handleWizardJump = (targetStep) => {
    if (targetStep <= maxUnlockedStep) {
      setStep(targetStep);
    }
  };

  const handleNextStep = () => {
    if (currentBlocker) return;

    const nextStep = Math.min(step + 1, STEPS.length - 1);

    setMaxUnlockedStep((previous) => Math.max(previous, nextStep));

    setStep(nextStep);
  };

  const handleBackStep = () => {
    setStep((previous) => Math.max(0, previous - 1));
  };

  /* ---------------------------------------------------------------------- */
  /*                                FETCHING                                */
  /* ---------------------------------------------------------------------- */

  const fetchCaseTypeAndCategory = useCallback(async () => {
    try {
      const res = await api.get(`${origin}/api/firmsetting-page/casetype-by-category/`);

      if (res.status === 200 && res.data) {
        setCaseTypesByCategory(res.data.data || {});
      }
    } catch (error) {
      console.error("Failed to load case types", error);
    }
  }, [origin]);

  /* ---------------------------------------------------------------------- */

  const fetchStates = useCallback(async () => {
    try {
      const res = await api.get(`${origin}/api/firmsetting-page/update-copilot-states/`);

      if (res.status === 200) {
        setStates([...(res.data.states || [])].sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error("Failed to load states", error);
    }
  }, [origin]);

  /* ---------------------------------------------------------------------- */

  const fetchDropdowns = useCallback(
    async (page_id) => {
      if (!page_id) {
        setDropdowns([]);
        setDropdownId("");
        return;
      }

      const seq = ++dropdownsReq.current;

      try {
        const res = await api.get(
          `${origin}/api/firmsetting-page/upload-dl-template/?page_id=${page_id}`
        );

        if (seq !== dropdownsReq.current) {
          return;
        }

        const data = res.status === 200 ? res.data.dropdown_data || [] : [];

        setDropdowns(data);

        /*
         * Deliberately DON'T automatically choose
         * the first dropdown.
         *
         * The user must make/confirm the selection.
         */
        setDropdownId("");
      } catch (error) {
        if (seq !== dropdownsReq.current) {
          return;
        }

        console.error("Failed to load dropdowns", error);

        setDropdowns([]);
        setDropdownId("");
      }
    },
    [origin]
  );

  /* ---------------------------------------------------------------------- */

  const fetchPages = useCallback(async () => {
    const seq = ++pagesReq.current;

    setPagesLoading(true);

    try {
      const res = await api.get(`${origin}/api/firmsetting-page/pages-with-dropdowns/`);

      if (seq !== pagesReq.current) {
        return;
      }

      const pages = res.status === 200 ? res.data || [] : [];

      const matching = pages.filter((page) =>
        page.case_types?.some((ct) => selectedCaseTypes.includes(ct.id))
      );

      if (!matching.length) {
        setOptions([]);
        setPageId("");
        setDropdowns([]);
        setDropdownId("");
        return;
      }

      const mappedOptions = matching.map((page) => ({
        value: page.id,
        name: page.name,
        icon: page.page_icon,

        label: (
          <div className="template-select-option">
            {page.page_icon && <img src={page.page_icon} alt="" />}

            <span>{page.name}</span>
          </div>
        ),
      }));

      setOptions(mappedOptions);

      /*
       * Preserve the page only when it is still
       * valid after an upstream case-type change.
       *
       * Otherwise require an explicit page selection.
       */
      const keep = matching.find((page) => page.id === pageId);

      if (!keep) {
        setPageId("");
        setDropdowns([]);
        setDropdownId("");
      }
    } catch (error) {
      if (seq !== pagesReq.current) {
        return;
      }

      console.error("Failed to load pages", error);

      setOptions([]);
      setPageId("");
      setDropdowns([]);
      setDropdownId("");
    } finally {
      if (seq === pagesReq.current) {
        setPagesLoading(false);
      }
    }

    // pageId is intentionally read for continuity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, selectedCaseTypes]);

  /* ---------------------------------------------------------------------- */
  /*                               INITIAL LOAD                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    fetchStates();
    fetchCaseTypeAndCategory();
  }, [fetchStates, fetchCaseTypeAndCategory]);

  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (selectedCaseTypes.length) {
      fetchPages();
    } else {
      pagesReq.current++;

      setOptions([]);
      setPageId("");
      setDropdowns([]);
      setDropdownId("");
    }
  }, [selectedCaseTypes, fetchPages]);

  /*
   * If Federal is selected before the states request
   * finishes, populate all state IDs once states arrive.
   */
  useEffect(() => {
    if (jurisdiction === "Federal" && states.length) {
      setSelectedStates(states.map((stateItem) => stateItem.id));
    }
  }, [jurisdiction, states]);

  /* ---------------------------------------------------------------------- */
  /*                                HANDLERS                                */
  /* ---------------------------------------------------------------------- */

  const toggleCaseType = (id) => {
    /*
     * An upstream choice changed.
     * Venue, page and document must be reconfirmed.
     */
    lockAfterStep(0);

    setSelectedCaseTypes((previous) =>
      previous.includes(id) ? previous.filter((item) => item !== id) : [...previous, id]
    );
  };

  const toggleAllCaseTypes = () => {
    lockAfterStep(0);
    setSelectedCaseTypes(allCaseTypesSelected ? [] : allCaseTypeIds);
  };

  /* ---------------------------------------------------------------------- */

  /* ---------------------------------------------------------------------- */

  const toggleState = (id) => {
    lockAfterStep(1);

    setSelectedStates((previous) =>
      previous.includes(id) ? previous.filter((item) => item !== id) : [...previous, id]
    );
  };

  const toggleAllStates = () => {
    lockAfterStep(1);
    setSelectedStates(allStatesSelected ? [] : states.map((stateItem) => stateItem.id));
  };

  /* ---------------------------------------------------------------------- */

  const handleJurisdictionChange = (value) => {
    lockAfterStep(1);

    setJurisdiction(value);

    if (value === "Federal") {
      setSelectedStates(states.map((stateItem) => stateItem.id));
    } else {
      setSelectedStates([]);
    }
  };

  /* ---------------------------------------------------------------------- */

  const handlePageChange = (option) => {
    if (!option?.value) {
      setPageId("");
      setDropdowns([]);
      setDropdownId("");
      lockAfterStep(2);
      return;
    }

    lockAfterStep(2);

    setPageId(option.value);
    setDropdownId("");

    fetchDropdowns(option.value);
  };

  /* ---------------------------------------------------------------------- */

  const handleDropdownChange = (value) => {
    lockAfterStep(2);

    setDropdownId(value);
  };

  /* ---------------------------------------------------------------------- */

  const acceptFile = (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".docx")) {
      setSubmitError("Templates must be .docx files.");

      return;
    }

    setSubmitError("");
    setUploadedFile(file);

    /*
     * Helpful default:
     * use the filename as the template name
     * when the user hasn't entered a name yet.
     */
    if (!templateName.trim()) {
      setTemplateName(file.name.replace(/\.docx$/i, ""));
    }
  };

  /* ---------------------------------------------------------------------- */
  /*                                  RESET                                 */
  /* ---------------------------------------------------------------------- */

  const resetWizard = () => {
    setTemplateName("");
    setSelectedCaseTypes([]);
    setSelectedStates([]);
    setUploadedFile(null);

    setJurisdiction("State");

    setPageId("");
    setDropdownId("");

    setOptions([]);
    setDropdowns([]);

    setCaseTypeQuery("");
    setStateQuery("");

    setSubmitError("");

    setStep(0);
    setMaxUnlockedStep(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* ---------------------------------------------------------------------- */
  /*                                  UPLOAD                                */
  /* ---------------------------------------------------------------------- */

  const handleUploadTemplate = async () => {
    if (!allStepsValid || submitting) {
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    const formData = new FormData();

    formData.append("name", templateName.trim());

    formData.append("case_id", caseId);

    formData.append("j_type", jurisdiction);

    formData.append("page_id", pageId);

    formData.append("dropdown_id", dropdownId);

    formData.append("file", uploadedFile);

    formData.append("case_types", JSON.stringify(selectedCaseTypes));

    formData.append("states", JSON.stringify(selectedStates));

    try {
      await uploadLetterTemplate(formData);

      resetWizard();

      handleTabChange("letter_templates");
    } catch (error) {
      console.error("Upload failed", error);

      setSubmitError("Upload failed. Check the file and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /*                             DERIVED DISPLAY                            */
  /* ---------------------------------------------------------------------- */

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(pageId)) || {},
    [options, pageId]
  );

  /* ---------------------------------------------------------------------- */

  const filteredCategories = useMemo(() => {
    const query = caseTypeQuery.trim().toLowerCase();

    return Object.entries(caseTypesByCategory)
      .map(([category, list]) => [
        category,

        query
          ? (list || []).filter((caseType) => caseType.name?.toLowerCase().includes(query))
          : list || [],
      ])
      .filter(([, list]) => list.length);
  }, [caseTypesByCategory, caseTypeQuery]);

  /* ---------------------------------------------------------------------- */

  const filteredStates = useMemo(() => {
    const query = stateQuery.trim().toLowerCase();

    return query
      ? states.filter((stateItem) => stateItem.name?.toLowerCase().includes(query))
      : states;
  }, [states, stateQuery]);

  /* ---------------------------------------------------------------------- */
  /*                                  RENDER                                */
  /* ---------------------------------------------------------------------- */

  return (
    <div className="upload-letter-template-wizard">
      <StepRail
        current={step}
        maxUnlockedStep={maxUnlockedStep}
        validity={validity}
        onJump={handleWizardJump}
      />

      <div
        style={{
          minHeight: "25px",
        }}
        className="template-wizard-footer pb-0 pt-1 px-0 template-wizard-controls"
      >
        <div
          className={`template-wizard-message p-0 ${
            currentBlocker || submitError
              ? "template-wizard-message-required"
              : "template-wizard-message-complete"
          }`}
        >
          <span className="template-wizard-message-icon">
            {currentBlocker || submitError ? "!" : "✓"}
          </span>
          <span>{submitError || currentBlocker || "This step is complete. You can continue."}</span>
        </div>

        <div className="template-wizard-actions">
          <button
            type="button"
            className="btn btn-outline-secondary height-25 template-wizard-button"
            disabled={step === 0 || submitting}
            onClick={handleBackStep}
          >
            ← Back
          </button>

          {isLastStep ? (
            <button
              type="button"
              className={`btn height-25 template-wizard-button template-wizard-primary-button ${
                allStepsValid && !submitting ? "btn-success" : "btn-secondary"
              }`}
              disabled={!allStepsValid || submitting}
              onClick={handleUploadTemplate}
            >
              {submitting ? "Uploading…" : "Upload Template"}
            </button>
          ) : (
            <button
              type="button"
              className={`btn height-25 template-wizard-button template-wizard-primary-button ${
                !currentBlocker && !submitting ? "btn-success" : "btn-secondary"
              }`}
              disabled={!!currentBlocker || submitting}
              onClick={handleNextStep}
            >
              Continue to {STEPS[step + 1].label}
              <span className="template-next-arrow">→</span>
            </button>
          )}
        </div>
      </div>

      <div className="template-wizard-body invisible-scrollbar upload-template-tab-firmsetting">
        {/* ================================================================ */}
        {/* STEP 1 — CASE TYPES                                              */}
        {/* ================================================================ */}

        {step === 0 && (
          <div className="template-step-panel p-0">
            <div className="template-step-toolbar pe-1">
              <SearchBox
                value={caseTypeQuery}
                onChange={setCaseTypeQuery}
                placeholder="Filter case types"
              />
              <div className="template-toolbar-actions">
                <button
                  type="button"
                  className="btn btn-link p-0 template-text-action"
                  disabled={!allCaseTypeIds.length}
                  onClick={toggleAllCaseTypes}
                >
                  {allCaseTypesSelected ? "Clear all" : "Select all"}
                </button>
              </div>
            </div>

            {filteredCategories.length === 0 ? (
              <EmptyState>No case types match “{caseTypeQuery}”.</EmptyState>
            ) : (
              filteredCategories.map(([category, caseTypes]) => {
                return (
                  <div key={category} className="template-choice-group">
                    <div className="template-choice-group-header">
                      <div className="template-choice-group-title">
                        <strong>{category}</strong>
                      </div>
                    </div>

                    <div className="template-option-grid">
                      {caseTypes.map((caseType) => {
                        const checked = selectedCaseTypes.includes(caseType.id);

                        return (
                          <label
                            key={caseType.id}
                            className={`template-checkbox-option ${
                              checked ? "template-checkbox-option-selected" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="form-check mt-0 mb-0 cursor-pointer"
                              checked={checked}
                              onChange={() => toggleCaseType(caseType.id)}
                            />

                            {caseType.casetype_icon && (
                              <span className="template-case-icon">
                                <img src={caseType.casetype_icon} alt="" />
                              </span>
                            )}

                            <span className="template-checkbox-label">{caseType.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 2 — VENUE / STATES                                          */}
        {/* ================================================================ */}

        {step === 1 && (
          <div className="template-step-panel p-0">
            <div className="template-field-row">
              <label htmlFor="jurisdictionSelect" className="template-field-label">
                Case jurisdiction
              </label>

              <div className="template-field-control custom-select-new-provider">
                <Form.Control
                  id="jurisdictionSelect"
                  as="select"
                  className="custom-select-options p-l-5 height-25 py-0"
                  value={jurisdiction}
                  onChange={(e) => handleJurisdictionChange(e.target.value)}
                >
                  <option value="State">State</option>

                  <option value="Federal">Federal — all states</option>
                </Form.Control>
              </div>
            </div>

            {isFederal ? (
              <div className="template-info-box">
                <strong>Federal jurisdiction selected</strong>

                <span>
                  This template will be available across all states. You do not need to select
                  states individually.
                </span>
              </div>
            ) : (
              <>
                <div className="template-step-toolbar template-state-toolbar pe-1">
                  <SearchBox
                    value={stateQuery}
                    onChange={setStateQuery}
                    placeholder="Filter states"
                  />
                  <div className="template-toolbar-actions">
                    <button
                      type="button"
                      className="btn btn-link p-0 template-text-action"
                      disabled={!states.length}
                      onClick={toggleAllStates}
                    >
                      {allStatesSelected ? "Clear all" : "Select all"}
                    </button>
                  </div>
                </div>

                <ul className="template-state-grid">
                  {filteredStates.map((stateItem) => {
                    const checked = selectedStates.includes(stateItem.id);

                    return (
                      <li key={stateItem.id}>
                        <label
                          htmlFor={`state-${stateItem.id}`}
                          className={`template-checkbox-option template-state-option ${
                            checked ? "template-checkbox-option-selected" : ""
                          }`}
                        >
                          <input
                            id={`state-${stateItem.id}`}
                            className="form-check mt-0 mb-0"
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleState(stateItem.id)}
                          />

                          <span className="template-state-icon">
                            {SPECIAL_STATES.includes(stateItem?.StateAbr) ? (
                              React.createElement(STATE_ICONS[stateItem.StateAbr])
                            ) : (
                              <svg className={`icon icon-state-${stateItem?.StateAbr}`}>
                                <use xlinkHref={`#icon-state-${stateItem?.StateAbr}`} />
                              </svg>
                            )}
                          </span>

                          <span className="template-checkbox-label">{stateItem?.name}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>

                {filteredStates.length === 0 && (
                  <EmptyState>No states match “{stateQuery}”.</EmptyState>
                )}
              </>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 3 — PAGE / DROPDOWN                                         */}
        {/* ================================================================ */}

        {step === 2 && (
          <div className="template-step-panel">
            {pagesLoading ? (
              <EmptyState>Loading available pages…</EmptyState>
            ) : !options.length ? (
              <div className="template-no-result-box">
                <strong>No matching page was found</strong>

                <span>None of the selected case types are attached to a page with dropdowns.</span>

                <button
                  type="button"
                  className="btn btn-link p-0 template-text-action"
                  onClick={() => setStep(0)}
                >
                  ← Go back to Case Types
                </button>
              </div>
            ) : (
              <>
                <div className="template-field-row">
                  <label className="template-field-label">Page</label>

                  <div className="template-custom-select-wrapper">
                    <CustomSelect
                      options={options}
                      colSize={12}
                      handlePageChange={handlePageChange}
                      selectedOption={selectedOption}
                      className=""
                    />
                  </div>
                </div>

                <div className="template-field-row">
                  <label htmlFor="dropdownSelect" className="template-field-label">
                    Dropdown
                  </label>

                  <div className="template-field-control custom-select-new-provider">
                    <Form.Control
                      id="dropdownSelect"
                      as="select"
                      className="custom-select-options height-25 p-l-5 py-0"
                      value={dropdownId}
                      disabled={!pageId || !dropdowns.length}
                      onChange={(e) => handleDropdownChange(e.target.value)}
                    >
                      {!pageId ? (
                        <option value="">Select a page first</option>
                      ) : dropdowns.length ? (
                        <>
                          <option value="">Select a dropdown</option>

                          {dropdowns.map((dropdown) => (
                            <option key={dropdown.id} value={dropdown.id}>
                              {dropdown.name}
                            </option>
                          ))}
                        </>
                      ) : (
                        <option value="">No dropdowns on this page</option>
                      )}
                    </Form.Control>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 4 — DOCUMENT                                                */}
        {/* ================================================================ */}

        {step === 3 && (
          <div className="template-step-panel">
            <div className="template-document-layout">
              <div className="template-document-main">
                <span className="template-guide">
                  <summary>How to build a dynamic letter template</summary>

                  <ol>
                    {GUIDE_STEPS.map((guideStep) => (
                      <li key={guideStep.title}>
                        <strong>{guideStep.title}</strong>

                        <div>{guideStep.body}</div>

                        {guideStep.notes && (
                          <ul>
                            {guideStep.notes.map((note) => (
                              <li key={note}>{note}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                </span>
                <div className="template-document-field">
                  <label className="template-document-label" htmlFor="templateName">
                    Template name
                    <span>*</span>
                  </label>

                  <input
                    id="templateName"
                    type="text"
                    className="form-control height-25 p-0 ps-1"
                    value={templateName}
                    placeholder="e.g. Demand Letter — Auto"
                    onChange={(e) => {
                      setTemplateName(e.target.value);

                      setSubmitError("");
                    }}
                  />
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  onDragOver={(e) => {
                    e.preventDefault();

                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();

                    setDragging(false);

                    acceptFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();

                      fileInputRef.current?.click();
                    }
                  }}
                  className={`template-dropzone ${dragging ? "template-dropzone-dragging" : ""} ${
                    uploadedFile ? "template-dropzone-has-file" : ""
                  }`}
                >
                  {uploadedFile ? (
                    <>
                      <div className="template-file-success-icon">✓</div>

                      <strong>{uploadedFile.name}</strong>

                      <span>{(uploadedFile.size / 1024).toFixed(0)} KB</span>

                      <small>Click or drop another .docx file to replace it.</small>
                    </>
                  ) : (
                    <>
                      <div className="template-upload-icon">↑</div>

                      <strong>Drop your .docx template here</strong>

                      <span>or click to browse</span>

                      <small>Microsoft Word .docx files only</small>
                    </>
                  )}

                  <input
                    type="file"
                    id="template_file"
                    name="file"
                    accept=".docx"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={(e) => acceptFile(e.target.files?.[0])}
                  />
                </div>

                {submitError && <div className="template-upload-error">{submitError}</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="template-wizard-footer template-wizard-controls">
        <div
          className={`template-wizard-message ${
            currentBlocker || submitError
              ? "template-wizard-message-required"
              : "template-wizard-message-complete"
          }`}
        >
          <span className="template-wizard-message-icon">
            {currentBlocker || submitError ? "!" : "✓"}
          </span>
          <span>{submitError || currentBlocker || "This step is complete. You can continue."}</span>
        </div>

        <div className="template-wizard-actions">
          <button
            type="button"
            className="btn btn-outline-secondary height-25 template-wizard-button"
            disabled={step === 0 || submitting}
            onClick={handleBackStep}
          >
            ← Back
          </button>

          {isLastStep ? (
            <button
              type="button"
              className={`btn height-25 template-wizard-button template-wizard-primary-button ${
                allStepsValid && !submitting ? "btn-success" : "btn-secondary"
              }`}
              disabled={!allStepsValid || submitting}
              onClick={handleUploadTemplate}
            >
              {submitting ? "Uploading…" : "Upload Template"}
            </button>
          ) : (
            <button
              type="button"
              className={`btn height-25 template-wizard-button template-wizard-primary-button ${
                !currentBlocker && !submitting ? "btn-success" : "btn-secondary"
              }`}
              disabled={!!currentBlocker || submitting}
              onClick={handleNextStep}
            >
              Continue to {STEPS[step + 1].label}
              <span className="template-next-arrow">→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadLetterTemplates;
